import { useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { captureException, captureMessage } from "@sentry/react";
import {
  lenderApi,
  useGetCommercialLendersQuery,
  useGetConsumerLendersQuery,
  useGetPersonalLendersQuery,
  useGetBusinessLendersQuery,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/apiSlice";
import { reusableFetch } from "@/utils/useFetch";
import type { UserSlice, UserState } from "@/store/slices/types/userSlice";

/**
 * This hook does heavy crunching by fetching 4 queries and then filtering the lenders.
 * It returns the added lenders and the remaining lenders.
 * This hook should only be called when necessary.
 */
const useLenderList = () => {
  const { lenderSettings } = useSelector<UserSlice, UserState>(({ user }) => user);
  const dispatch = useDispatch();

  // Queries.
  const { data: consumerData, isFetching: consumerIsFetching } = useGetConsumerLendersQuery();
  const { data: personalData, isFetching: personalIsFetching } = useGetPersonalLendersQuery();
  const { data: commercialData, isFetching: commercialIsFetching } = useGetCommercialLendersQuery();
  const { data: businessData, isFetching: businessIsFetching } = useGetBusinessLendersQuery();

  // Lender list filters.
  const lendersList = useMemo(() => {
    if (
      consumerData &&
      !consumerIsFetching &&
      personalData &&
      !personalIsFetching &&
      commercialData &&
      !commercialIsFetching &&
      businessData &&
      !businessIsFetching
    ) {
      return [
        ...consumerData?.data?.getAllLendersData?.docs,
        ...personalData?.data?.getAllLendersData?.docs,
        ...commercialData?.data?.getAllLendersData?.docs,
        ...businessData?.data?.getAllLendersData?.docs,
      ];
    }
    return [];
  }, [
    consumerData,
    consumerIsFetching,
    personalData,
    personalIsFetching,
    commercialData,
    commercialIsFetching,
    businessData,
    businessIsFetching,
  ]);

  useEffect(() => {
    const request = window.indexedDB.open("financeableDB");
    let queries = {};
    request.onsuccess = async (event) => {
      const db = request.result;
      const objectStore = db.transaction(db.objectStoreNames[0], "readonly").objectStore("keyvaluepairs");
      const objectStoreData = objectStore.get("persist:root");

      objectStoreData.onsuccess = (e) => {
        if (objectStoreData.result) {
          const data = JSON.parse(objectStoreData.result ?? "");
          const lenderApiData = JSON.parse(data.lenderApi ?? "");
          queries = lenderApiData.queries;
          Object.keys(queries ?? {}).forEach(async (query) => {
            // @ts-expect-error: ignoring as it is existing js file
            const queryTimestamp = Math.floor(queries[query].fulfilledTimeStamp / 1000) * 1000;
            // @ts-expect-error: ignoring as it is existing js file
            if (["pending", "rejected"].includes(queries[query].status) || (await logTimestamp()) > queryTimestamp) {
              dispatch(lenderApi.endpoints.getConsumerLenders.initiate({}, { subscribe: true, forceRefetch: true }));
              dispatch(lenderApi.endpoints.getPersonalLenders.initiate({}, { subscribe: true, forceRefetch: true }));
              dispatch(lenderApi.endpoints.getCommercialLenders.initiate({}, { subscribe: true, forceRefetch: true }));
              dispatch(lenderApi.endpoints.getBusinessLenders.initiate({}, { subscribe: true, forceRefetch: true }));
            }
          });
        }
      };
    };

    request.onerror = (error) => {
      captureMessage("Error connecting to IndexedDB");
      captureException(error);
    };

    const logTimestamp = async () => {
      // @ts-expect-error: ignoring as this is existing code
      const { data } = await reusableFetch(`lender/log`, "GET", null);
      const lenderLog = data.data.lenderLog;
      const logTimestamp = Math.floor(new Date(lenderLog?.createdAt || 0).getTime() / 1000) * 1000;
      return logTimestamp;
    };
  }, []);

  const filteredLendersList = lendersList.filter(
    (obj, index, self) => index === self.findIndex((item) => item.lender === obj.lender),
  );

  // Added lenders.
  const addedLendersList = lenderSettings.map((setting) => {
    const logo = filteredLendersList.find(
      ({ lender }) => lender === setting.lender || (lender === "Dynamoney" && setting.lender === "Grow"),
    );
    return { logo: logo?.logoURL, ...setting, lenderCredentials: logo?.lenderCredentials };
  });

  // Remaining lenders to be added.
  const filterAndSortRemainingLenders = useMemo(() => {
    const lenserSettingNames = lenderSettings?.map(({ lender }) => lender);
    const remaininingLenders = filteredLendersList?.filter((lender) => !lenserSettingNames?.includes(lender.lender));
    return remaininingLenders.sort((a, b) => {
      return a.lender.toLowerCase() > b.lender.toLowerCase() ? 1 : -1;
    });
  }, [filteredLendersList, lenderSettings]);

  // Remaining lenders to be added.
  const remainingLendersList = filterAndSortRemainingLenders;

  return {
    addedLendersList,
    remainingLendersList,
    consumerIsFetching,
    personalIsFetching,
    commercialIsFetching,
    businessIsFetching,
  };
};

export { useLenderList };
